import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import type { ArDisease, ArIndexAllEntry, ArList } from '../../types';
import {
  GlobalAssetEntry, TrackerRow, PHASE_ORDER, REGION_COLOR, buildGlobalAssets, metricsFor,
} from './arHelpers';

/** The "By target" cross-disease asset explorer: ported from the reference Asset Radar artifact's
 * globalAssets()/FACETS/passT() (facet sidebar with live faceted counts, free-text search, sort, asset
 * cards with Save/Compare), sourced from our own /api/index-all + /api/lists instead of the artifact's
 * own Google Sheets fetch + Firestore lists.
 *
 * Deliberately simplified vs. the reference: /api/index-all returns every disease's workbook in one call
 * (our backend already assembles this server-side), so there's no per-disease progressive "N of M done"
 * loader here -- just a single loading spinner, then a static "N landscapes read" summary. Saved lists
 * use our own SQL-backed RadarList CRUD (asset_radar/api.py's /api/lists) rather than the reference's
 * real-time Firestore org-sharing layer: "mine" lists only, no live multi-user sync, no sharing UI (the
 * backend supports sharing -- just not exposed here yet). */

type FacetId = 'targets' | 'moas' | 'mods' | 'phases' | 'diseases' | 'regions' | 'data';

interface Facet {
  id: FacetId;
  title: string;
  searchable: boolean;
  values: (e: GlobalAssetEntry) => string[];
}

const FACETS: Facet[] = [
  { id: 'targets', title: 'Target', searchable: true, values: (e) => e.targets },
  { id: 'moas', title: 'Mechanism of action', searchable: true, values: (e) => [e.moa] },
  { id: 'mods', title: 'Modality', searchable: false, values: (e) => [e.modality] },
  { id: 'phases', title: 'Most advanced phase', searchable: false, values: (e) => [e.phase] },
  { id: 'diseases', title: 'Disease', searchable: true, values: (e) => (e.diseases.size ? [...e.diseases] : ['Not in a loaded landscape']) },
  { id: 'regions', title: 'Sponsor region', searchable: false, values: (e) => [e.region] },
  { id: 'data', title: 'Clinical data', searchable: false, values: (e) => [e.dataState] },
];
const DEFAULT_OPEN = new Set<FacetId>(['targets', 'mods', 'phases']);

type SortKey = 'phase' | 'data' | 'name';
const SORTS: [SortKey, string][] = [['phase', 'Most advanced first'], ['data', 'Most datasets first'], ['name', 'A to Z']];
const phaseRank = (e: GlobalAssetEntry) => { const i = PHASE_ORDER.indexOf(e.phase); return i < 0 ? PHASE_ORDER.length : i; };
const SORT_FN: Record<SortKey, (a: GlobalAssetEntry, b: GlobalAssetEntry) => number> = {
  phase: (a, b) => phaseRank(a) - phaseRank(b) || b.apps.length - a.apps.length || a.name.localeCompare(b.name),
  data: (a, b) => b.apps.length - a.apps.length || phaseRank(a) - phaseRank(b) || a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
};

const PAGE = 40;

interface Props {
  diseases: ArDisease[];
  tracker: TrackerRow[] | null;
  onJumpToIndication: (opts: { disease: string; setting: string; asset: string }) => void;
}

const ByTargetView: React.FC<Props> = ({ diseases, tracker, onJumpToIndication }) => {
  const [entries, setEntries] = useState<ArIndexAllEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = (fresh: boolean) => {
    setLoading(true); setError(null);
    if (fresh) apiService.arClearRadarCache();
    apiService.arGetIndexAll()
      .then((rows) => setEntries(rows))
      .catch((e: any) => setError(e?.response?.data?.error || e?.message || 'Could not load the asset index'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const diseaseLabels = useMemo(() => new Map(diseases.map((d) => [d.key, d.label])), [diseases]);
  const globalAssets = useMemo(
    () => buildGlobalAssets(entries || [], diseaseLabels, tracker),
    [entries, diseaseLabels, tracker],
  );

  // ---- free-text search ----
  const [q, setQ] = useState('');
  const qTokens = useMemo(() => q.trim().toLowerCase().split(/\s+/).filter(Boolean), [q]);
  const matchesQ = (e: GlobalAssetEntry) => qTokens.every((t) => e.hay.includes(t));

  // ---- facets ----
  const [selected, setSelected] = useState<Record<FacetId, Set<string>>>(
    () => Object.fromEntries(FACETS.map((f) => [f.id, new Set<string>()])) as Record<FacetId, Set<string>>,
  );
  const [facetOpen, setFacetOpen] = useState<Partial<Record<FacetId, boolean>>>({});
  const [facetSearch, setFacetSearch] = useState<Partial<Record<FacetId, string>>>({});
  const [facetShowAll, setFacetShowAll] = useState<Partial<Record<FacetId, boolean>>>({});

  const passFacets = (e: GlobalAssetEntry, skip: FacetId | null) => {
    if (!matchesQ(e)) return false;
    for (const f of FACETS) {
      if (f.id === skip) continue;
      const sel = selected[f.id];
      if (sel.size && !f.values(e).some((v) => sel.has(v))) return false;
    }
    return true;
  };

  const toggleFacetValue = (id: FacetId, value: string) => {
    setSelected((prev) => {
      const next = new Set(prev[id]);
      if (next.has(value)) next.delete(value); else next.add(value);
      return { ...prev, [id]: next };
    });
    setLimit(PAGE);
  };
  const clearFacet = (id: FacetId) => { setSelected((prev) => ({ ...prev, [id]: new Set() })); setLimit(PAGE); };

  // ---- sort + pagination ----
  const [sort, setSort] = useState<SortKey>('phase');
  const [limit, setLimit] = useState(PAGE);

  // ---- lists ----
  const [lists, setLists] = useState<ArList[]>([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const refreshLists = () => apiService.arListLists().then((r) => { setLists(r.mine.filter((l) => l.kind === 'asset')); setListsLoaded(true); }).catch(() => setListsLoaded(true));
  useEffect(() => { refreshLists(); }, []);
  const [selectedListId, setSelectedListId] = useState<number | 'all'>('all');
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const activeList = selectedListId === 'all' ? null : lists.find((l) => l.id === selectedListId) || null;

  const createList = async (name: string, firstAsset?: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const lst = await apiService.arCreateList('asset', trimmed, firstAsset ? [firstAsset] : []);
      setLists((prev) => [...prev, lst]);
      setNewListName(''); setNewListOpen(false);
      return lst;
    } catch { /* ignore -- the popover/bar just won't show a new list */ }
  };

  const toggleAssetInList = async (list: ArList, assetKey: string) => {
    const has = list.assets.includes(assetKey);
    const nextAssets = has ? list.assets.filter((k) => k !== assetKey) : [...list.assets, assetKey];
    setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, assets: nextAssets } : l)));
    try { await apiService.arPatchList(list.id, { assets: nextAssets }); } catch { refreshLists(); }
  };

  // ---- compare ----
  const [compareKeys, setCompareKeys] = useState<Set<string>>(new Set());
  const [comparing, setComparing] = useState(false);
  const toggleCompare = (key: string) => setCompareKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else if (next.size < 4) next.add(key);
    return next;
  });

  const assetsByKey = useMemo(() => new Map(globalAssets.map((e) => [e.key, e])), [globalAssets]);

  let visible = globalAssets.filter((e) => passFacets(e, null));
  if (activeList) visible = visible.filter((e) => activeList.assets.includes(e.key));
  visible = visible.slice().sort(SORT_FN[sort]);

  if (loading) {
    return <div className="state"><div className="spin" /><p>Loading database…</p></div>;
  }
  if (error) {
    return <div className="state"><h2>Couldn&rsquo;t load the asset index</h2><p>{error}</p><button type="button" className="btn" onClick={() => load(false)}>Try again</button></div>;
  }

  const diseasesRead = entries?.length || 0;

  return (
    <div className="tx">
      <aside className="tx-side">
        <div className="tx-shd">
          <span className="tx-sht">Filters</span>
        </div>
        <input
          type="search" className="tx-q" value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }}
          placeholder="Describe what you want, e.g. TROP2 ADC China" aria-label="Search assets"
        />
        {FACETS.map((f) => (
          <FacetBox
            key={f.id}
            facet={f}
            allAssets={globalAssets}
            passFacets={passFacets}
            selected={selected[f.id]}
            open={facetOpen[f.id] ?? (DEFAULT_OPEN.has(f.id) || selected[f.id].size > 0)}
            onToggleOpen={() => setFacetOpen((prev) => ({ ...prev, [f.id]: !(prev[f.id] ?? (DEFAULT_OPEN.has(f.id) || selected[f.id].size > 0)) }))}
            search={facetSearch[f.id] || ''}
            onSearch={(v) => setFacetSearch((prev) => ({ ...prev, [f.id]: v }))}
            showAll={!!facetShowAll[f.id]}
            onToggleShowAll={() => setFacetShowAll((prev) => ({ ...prev, [f.id]: !prev[f.id] }))}
            onToggleValue={(v) => toggleFacetValue(f.id, v)}
            onClear={() => clearFacet(f.id)}
          />
        ))}
      </aside>

      <div>
        <div className="tx-top">
          <h2>Find assets by target and mechanism</h2>
          <details className="lrep">
            <summary>{diseasesRead} landscape{diseasesRead === 1 ? '' : 's'} read.</summary>
            <ul>
              {(entries || []).map((d) => {
                const wb = d.parsed;
                const assetCount = (wb?.inds || []).reduce((n, ind) => n + (ind.land?.assets.length || 0), 0);
                const trialCount = (wb?.inds || []).reduce((n, ind) => n + (ind.cal?.length || 0), 0);
                return <li key={d.disease}>{d.title}: {assetCount} assets in {(wb?.inds || []).length} settings, {trialCount} trials</li>;
              })}
            </ul>
          </details>
          <div className="spacer" />
          <button type="button" className="btn" onClick={() => load(true)}>Refresh</button>
        </div>

        <div className="tx-top">
          <span className="meta">{visible.length} asset{visible.length === 1 ? '' : 's'}</span>
          <div className="spacer" />
          <select className="pill" aria-label="Sort" value={sort} onChange={(e) => { setSort(e.target.value as SortKey); setLimit(PAGE); }}>
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        <div className="lbar">
          <span className="lic">My lists</span>
          <button type="button" className="chip" aria-pressed={selectedListId === 'all'} onClick={() => setSelectedListId('all')}>All assets</button>
          {listsLoaded && lists.map((l) => (
            <button key={l.id} type="button" className="chip" aria-pressed={selectedListId === l.id} onClick={() => setSelectedListId(l.id)}>
              {l.name} <span className="chipn">{l.assets.length}</span>
            </button>
          ))}
          {newListOpen ? (
            <span className="sp-new" style={{ border: 0, padding: 0 }}>
              <input
                className="sp-in" autoFocus value={newListName} placeholder="List name"
                onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createList(newListName); if (e.key === 'Escape') setNewListOpen(false); }}
              />
              <button type="button" className="btn" onClick={() => createList(newListName)}>Add</button>
            </span>
          ) : (
            <button type="button" className="chip" onClick={() => setNewListOpen(true)}>+ New list</button>
          )}
          <span className="lnote meta">Use &#9734; Save on any asset to start a list.</span>
        </div>

        {!visible.length ? (
          <div className="state"><h2>No assets match these filters</h2></div>
        ) : (
          <div className="tcards">
            {visible.slice(0, limit).map((e) => (
              <AssetCard
                key={e.key}
                e={e}
                lists={lists}
                comparing={comparing}
                compareSelected={compareKeys.has(e.key)}
                compareFull={compareKeys.size >= 4}
                onToggleCompare={() => toggleCompare(e.key)}
                onToggleList={toggleAssetInList}
                onCreateListWith={(name) => createList(name, e.key)}
                onJump={onJumpToIndication}
              />
            ))}
          </div>
        )}
        {visible.length > limit && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button type="button" className="btn" onClick={() => setLimit((v) => v + PAGE)}>Show more ({visible.length - limit} left)</button>
          </div>
        )}
      </div>

      {compareKeys.size > 0 && (
        <div className="tray">
          <strong>Compare</strong>
          {[...compareKeys].map((k) => {
            const e = assetsByKey.get(k);
            if (!e) return null;
            return (
              <span key={k} className="tc">
                {e.name}
                <button type="button" aria-label={`Remove ${e.name}`} onClick={() => toggleCompare(k)}>&#10005;</button>
              </span>
            );
          })}
          <div className="spacer" />
          <button type="button" className="btn dark" disabled={compareKeys.size < 2} onClick={() => setComparing(true)}>
            Compare {compareKeys.size} side by side
          </button>
        </div>
      )}

      {comparing && (
        <CompareTable
          assets={[...compareKeys].map((k) => assetsByKey.get(k)).filter((x): x is GlobalAssetEntry => !!x)}
          onClose={() => setComparing(false)}
        />
      )}
    </div>
  );
};

const FacetBox: React.FC<{
  facet: Facet;
  allAssets: GlobalAssetEntry[];
  passFacets: (e: GlobalAssetEntry, skip: FacetId | null) => boolean;
  selected: Set<string>;
  open: boolean;
  onToggleOpen: () => void;
  search: string;
  onSearch: (v: string) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
  onToggleValue: (v: string) => void;
  onClear: () => void;
}> = ({ facet, allAssets, passFacets, selected, open, onToggleOpen, search, onSearch, showAll, onToggleShowAll, onToggleValue, onClear }) => {
  const base = useMemo(() => allAssets.filter((e) => passFacets(e, facet.id)), [allAssets, passFacets, facet.id]);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of base) for (const v of facet.values(e)) m.set(v, (m.get(v) || 0) + 1);
    let opts = [...m.entries()];
    opts = facet.id === 'phases'
      ? opts.sort((a, b) => PHASE_ORDER.indexOf(a[0]) - PHASE_ORDER.indexOf(b[0]))
      : opts.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return opts;
  }, [base, facet]);

  const filtered = search.trim()
    ? counts.filter(([v]) => v.toLowerCase().includes(search.trim().toLowerCase()))
    : counts;
  const asChips = !facet.searchable && filtered.length <= 7;
  const shown = showAll ? filtered : filtered.slice(0, 8);

  const summary = selected.size
    ? [...selected].slice(0, 2).join(', ') + (selected.size > 2 ? ` +${selected.size - 2}` : '')
    : `${counts.length} option${counts.length === 1 ? '' : 's'}`;

  return (
    <div className={`facet${open ? '' : ' collapsed'}`}>
      <div className="fhd">
        <button type="button" className="fhd-t" onClick={onToggleOpen} aria-expanded={open}>
          <span className="fchev" aria-hidden="true">&#9656;</span>
          <span className="fttl">{facet.title}</span>
        </button>
        {selected.size > 0 && <span className="fbadge">{selected.size}</span>}
        {!open && <span className="fsum">{summary}</span>}
        {open && selected.size > 0 && <button type="button" className="fclr" onClick={onClear}>Clear</button>}
      </div>
      {open && (
        asChips ? (
          <div className="fchips">
            {filtered.map(([v, n]) => (
              <button key={v} type="button" className={`fchip${n === 0 ? ' zero' : ''}`} aria-pressed={selected.has(v)} onClick={() => onToggleValue(v)}>
                {v} <em>{n}</em>
              </button>
            ))}
          </div>
        ) : (
          <>
            {facet.searchable && (
              <input type="search" value={search} onChange={(e) => onSearch(e.target.value)} placeholder={`Find a ${facet.title.toLowerCase()}`} aria-label={`Find a ${facet.title.toLowerCase()}`} />
            )}
            <div className="flist">
              {shown.map(([v, n]) => (
                <label key={v} className={`fopt${selected.has(v) ? ' on' : ''}${n === 0 ? ' zero' : ''}`}>
                  <input type="checkbox" checked={selected.has(v)} onChange={() => onToggleValue(v)} />
                  <span>{v}</span>
                  <em>{n}</em>
                </label>
              ))}
            </div>
            {filtered.length > 8 && (
              <button type="button" className="fmore" onClick={onToggleShowAll}>
                {showAll ? 'Show less' : `Show all ${filtered.length}`}
              </button>
            )}
          </>
        )
      )}
    </div>
  );
};

const AssetCard: React.FC<{
  e: GlobalAssetEntry;
  lists: ArList[];
  comparing: boolean;
  compareSelected: boolean;
  compareFull: boolean;
  onToggleCompare: () => void;
  onToggleList: (list: ArList, assetKey: string) => void;
  onCreateListWith: (name: string) => void;
  onJump: (opts: { disease: string; setting: string; asset: string }) => void;
}> = ({ e, lists, compareSelected, compareFull, onToggleCompare, onToggleList, onCreateListWith, onJump }) => {
  const [popOpen, setPopOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const popRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!popOpen) return;
    const onDoc = (ev: MouseEvent) => { if (popRef.current && !popRef.current.contains(ev.target as Node)) setPopOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [popOpen]);

  const inAnyList = lists.some((l) => l.assets.includes(e.key));

  const appKey = (app: { disease: string; ind: { key: string } }) => `${app.disease}|${app.ind.key}`;
  const datasets = e.apps
    .map((app) => ({ app, metrics: metricsFor(app.ind.land, app.a) }))
    .filter((d) => d.metrics.length > 0);
  const [showAllDatasets, setShowAllDatasets] = useState(false);
  const shownDatasets = showAllDatasets ? datasets.slice(0, 20) : datasets.slice(0, 1);

  // One chip per distinct disease+setting: clickable (landscape) chips first, then a dashed non-clickable
  // chip for any disease+setting that's only in a calendar/trial, skipping ones already shown.
  const seenChips = new Set<string>();
  const appChips = e.apps.filter((app) => { const k = appKey(app); if (seenChips.has(k)) return false; seenChips.add(k); return true; });
  const trialChips = e.trials.filter((t) => { const k = appKey(t); if (seenChips.has(k)) return false; seenChips.add(k); return true; });

  return (
    <article className={`tcard${compareSelected ? ' sel' : ''}`}>
      <div className="tc-h">
        <div className="savewrap" ref={popRef}>
          <button type="button" className={`btn savebtn${inAnyList ? ' saved' : ''}`} aria-haspopup="true" aria-expanded={popOpen} onClick={() => setPopOpen((v) => !v)}>
            <span className="lstar" aria-hidden="true">&#9733;</span> {inAnyList ? `Saved (${lists.filter((l) => l.assets.includes(e.key)).length})` : 'Save'}
          </button>
          {popOpen && (
            <div className="savepop">
              <div className="sp-h">Save to a list</div>
              <div className="sp-list">
                {lists.length ? lists.map((l) => (
                  <label key={l.id} className="sp-opt">
                    <input type="checkbox" checked={l.assets.includes(e.key)} onChange={() => onToggleList(l, e.key)} />
                    <span>{l.name}</span>
                    <em>{l.assets.length}</em>
                  </label>
                )) : <p className="sp-note">No lists yet — create one below.</p>}
              </div>
              <div className="sp-new">
                <input className="sp-in" value={newName} placeholder="New list name" onChange={(ev) => setNewName(ev.target.value)} onKeyDown={(ev) => { if (ev.key === 'Enter' && newName.trim()) { onCreateListWith(newName); setNewName(''); setPopOpen(false); } }} />
                <button type="button" className="btn" onClick={() => { if (newName.trim()) { onCreateListWith(newName); setNewName(''); setPopOpen(false); } }}>Add</button>
              </div>
            </div>
          )}
        </div>
        <div className="grow">
          <h3>{e.name}</h3>
          {e.alias && <div className="alias">{e.alias}</div>}
        </div>
        <div className="atags">
          {e.lead && <span className="tag lead">Lead</span>}
          <span className="tag ph">{e.phase}</span>
        </div>
        <button type="button" className={`btn${compareSelected ? ' sel' : ''}`} aria-pressed={compareSelected} disabled={!compareSelected && compareFull} onClick={onToggleCompare}>
          {compareSelected ? 'Selected' : 'Compare'}
        </button>
      </div>

      <div>
        <div><span className="cap">Sponsor</span> {e.sponsor} <span className="chip" style={{ color: REGION_COLOR[e.region], borderColor: REGION_COLOR[e.region] }}>{e.region}</span></div>
        <div><span className="cap">Target</span> {e.target || 'Not stated'}</div>
        <div><span className="cap">Mechanism</span> {e.moa}</div>
        <div><span className="cap">Diseases</span> {e.diseases.size ? [...e.diseases].join(', ') : 'Not in a loaded landscape'}</div>
      </div>

      <div className="tc-data">
        {datasets.length ? (
          <>
            {shownDatasets.map(({ app, metrics }, i) => (
              <div className="ds" key={i}>
                <b>{app.disease}: {app.ind.label}</b>
                {metrics.map((m) => <div key={m.label}><span>{m.label}</span> {m.value}</div>)}
              </div>
            ))}
            {datasets.length > 1 && (
              <button type="button" className="fmore" onClick={() => setShowAllDatasets((v) => !v)}>
                {showAllDatasets ? 'Show less' : `Show all ${datasets.length} datasets`}
              </button>
            )}
          </>
        ) : e.apps.length ? (
          <p className="meta">On a landscape, but efficacy and Grade &#8805;3 rows are NR.</p>
        ) : e.trials.length ? (
          <p className="meta">No clinical data yet; {e.trials.length} trial{e.trials.length === 1 ? '' : 's'} on the calendars.</p>
        ) : (
          <p className="meta">No dataset in the landscapes read so far.</p>
        )}
      </div>

      <div className="tc-apps">
        {appChips.map((app) => (
          <button key={appKey(app)} type="button" onClick={() => onJump({ disease: app.disease, setting: app.ind.key, asset: app.a.name })}>
            {app.disease}: {app.ind.label}
          </button>
        ))}
        {trialChips.map((t) => (
          <span key={appKey(t)}>{t.disease}: {t.ind.label}</span>
        ))}
      </div>
    </article>
  );
};

const CompareTable: React.FC<{ assets: GlobalAssetEntry[]; onClose: () => void }> = ({ assets, onClose }) => {
  const firstDataset = (e: GlobalAssetEntry) => e.apps.map((app) => ({ app, metrics: metricsFor(app.ind.land, app.a) })).find((d) => d.metrics.length > 0) || null;
  const datasets = assets.map(firstDataset);
  const metricLabels = [...new Set(datasets.flatMap((d) => (d ? d.metrics.map((m) => m.label) : [])))];

  return (
    <div className="state" style={{ textAlign: 'left', marginTop: 12 }}>
      <div className="tx-top">
        <h2>Compare {assets.length} assets</h2>
        <div className="spacer" />
        <button type="button" className="btn" onClick={onClose}>Close</button>
      </div>
      <div className="tablewrap">
        <table className="land">
          <thead>
            <tr><th className="corner" scope="col" /> {assets.map((e) => <th key={e.key} scope="col">{e.name}</th>)}</tr>
          </thead>
          <tbody>
            <tr><th scope="row">Sponsor</th>{assets.map((e) => <td key={e.key}>{e.sponsor} ({e.region})</td>)}</tr>
            <tr><th scope="row">Target</th>{assets.map((e) => <td key={e.key}>{e.target || 'Not stated'}</td>)}</tr>
            <tr><th scope="row">Mechanism</th>{assets.map((e) => <td key={e.key}>{e.moa}</td>)}</tr>
            <tr><th scope="row">Modality</th>{assets.map((e) => <td key={e.key}>{e.modality}</td>)}</tr>
            <tr><th scope="row">Most advanced</th>{assets.map((e) => <td key={e.key}>{e.phase}</td>)}</tr>
            <tr><th scope="row">Diseases</th>{assets.map((e) => <td key={e.key}>{e.diseases.size ? [...e.diseases].join(', ') : 'Not in a loaded landscape'}</td>)}</tr>
            <tr><th scope="row">Dataset</th>{datasets.map((d, i) => <td key={i}>{d ? `${d.app.disease}: ${d.app.ind.label}` : '—'}</td>)}</tr>
            {metricLabels.map((label) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                {datasets.map((d, i) => <td key={i}>{d?.metrics.find((m) => m.label === label)?.value || '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="meta">Each column shows the asset&rsquo;s first landscape dataset; settings differ, so this is not a head-to-head comparison.</p>
    </div>
  );
};

export default ByTargetView;
